<script lang="ts">
	import {
		DashDeviceWidget,
		Loader,
		CircularProgress,
		toast,
		LabelV2,
		isOpaqueWhiteActiveBackground,
	} from '@Shelly/shelly-design-system/index';
	import type {
		ActiveSurfacePresetId,
		PressHoldOptions,
		SurfaceGlowConfig,
	} from '@Shelly/shelly-design-system';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import {
		is_gen_2,
		isCameraDevice,
		isFanDevice,
		isWallDisplay,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { updating_devices } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { getDeviceState, defaultStateType } from 'src/stores/device-runtime/states';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bulkActionEdit, edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { beforeUpdate, createEventDispatcher, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import DashDeviceQuickActions from './components/DashDeviceQuickActions.svelte';
	import MainControlStatusSlot from 'src/components/Lists/Devices/MainControlStatusSlot.svelte';
	import {
		getAdditionalText,
		getAvatar,
		formatTimerCountdown,
	} from 'src/components/Dashboards/dash-helpers';
	import DashCameraSingleDeviceCard from './DashCameraSingleDeviceCard.svelte';
	import type {
		DashCameraWidgetConfig,
		DashDeviceControlPlacement,
	} from 'src/components/Dashboards/dash-types';
	import {
		deviceControlOnOff,
		getDeviceOnOffState,
		getDeviceTimerState,
		deviceHasAddonSwitch,
	} from 'src/helpers/device_control_on_off';
	import type { DeviceOnOffState, DeviceTimerState } from 'src/helpers/device_control_on_off';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import QuickTimeControl from 'src/components/Controls/Single/QuickTimeControl.svelte';
	import { convertToSeconds } from 'src/helpers/timer_helpers';
	import type { Device } from 'src/types/Device';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { hasBleDashStatusInvalidatingNotification } from 'src/helpers/dash_device_ble_status';
	import { resolveDashDeviceInfoLabels } from 'src/helpers/dash_device_info_labels';
	import { getInputStateInfoLabel } from 'src/helpers/input_state_display';
	import { resolveDashDeviceActiveSurface } from 'src/helpers/dash_device_active_surface';
	import type { DashDeviceActiveSurfaceOverride } from 'src/helpers/dash_device_active_surface';
	import { getDashLightingPack } from 'src/helpers/dash-lighting';
	import { dashLightingPackId } from 'src/stores/features/dev/dash-lighting-pack';

	export let deviceWidget;
	export let editMode = false;
	export let disableSelection = false;
	export let showFullscreen = false;
	export let cameraConfig: DashCameraWidgetConfig | undefined;
	/** Whole-card tap (`card`, default) vs on/off button in the right slot (`button`). */
	export let controlPlacement: DashDeviceControlPlacement = 'card';
	/** Optional external active-surface preset (gallery / Customize Card). */
	export let activeSurfacePreset: ActiveSurfacePresetId | undefined;
	/** Optional raw surfaceGlow override; combined with preset when both set via override object. */
	export let surfaceGlowOverride: SurfaceGlowConfig | undefined;
	/** When true (default), lights / Cury use coloured glow from status/settings. */
	export let enableLightColor = true;
	export let lightColor: string | null | undefined;
	/** Optional long-press shortcut to enter list rearrange (room devices). */
	export let pressHoldOptions: PressHoldOptions | null | undefined;

	const dispatch = createEventDispatcher();

	let statuses;
	let notAuthenticated = false;
	let isLoading = false;
	let isActive = false;
	let showPinModal = false;
	let showQuickControlModal = false;
	let loadingRequest = false;

	let timerState: DeviceTimerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
	let tickInterval: ReturnType<typeof setInterval> | null = null;
	let cachedTimerStartedAt: number | undefined;

	function startTimerTick() {
		stopTimerTick();
		tickInterval = setInterval(() => {
			if (!device || !status || !settings) return;

			const nextTimerState = getDeviceTimerState(device, status, settings);
			timerState = nextTimerState;

			if (!nextTimerState.hasTimer || !nextTimerState.timer?.time) stopTimerTick();
		}, 50); // Set small interval for smooth circle fill/unfill
	}

	function stopTimerTick() {
		if (tickInterval) {
			clearInterval(tickInterval);
			tickInterval = null;
		}
	}

	async function executeToggle(flipBackSeconds?: number) {
		if (!onOffState) return;
		loadingRequest = true;

		try {
			const result = await deviceControlOnOff({
				...onOffState.controlParams,
				...(flipBackSeconds && { flipBackSeconds }),
			});

			if (!result.success) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
		} catch {
			toast.open({ type: 'danger', title: $format('error_global') });
		} finally {
			loadingRequest = false;
		}
	}

	function handleClick(e: MouseEvent) {
		e.stopPropagation();

		if (editMode || $edit) return;

		// Button placement: card body opens settings; on/off lives in the right slot.
		if (controlPlacement === 'button') {
			dispatch('click');
			return;
		}

		if (pinLocked) {
			showPinModal = true;
			return;
		}

		if (!onOffState || onOffState.readOnly || onOffState.disabled || loadingRequest || isLoading) {
			/**
			 * After latest discussion:
			 * The cards with no on/off logic should show sidepanel, when clicking on the card body or on the dots.
			 */
			dispatch('click');
			return;
		}

		executeToggle();
	}

	function handlePinUnlock() {
		unlockPin(device, pinLocked);
		executeToggle();
	}

	function handleTimerSelect(entry: { hr?: string; min: string; sec: string }) {
		const flipBackSeconds = convertToSeconds(entry?.hr || '00', entry.min, entry.sec);
		showQuickControlModal = false;
		executeToggle(flipBackSeconds);
	}

	function checkShouldHideRightSlot(
		device: Device,
		edit: boolean,
		onOffState: DeviceOnOffState,
		hasAddon: boolean,
		showRightSlot: boolean,
		isDefaultState: boolean,
		hasDashRightSlotLabel: boolean,
		suppressStatusLabels: boolean,
		placement: DashDeviceControlPlacement,
	) {
		if (!device || edit) {
			return true;
		}

		// Button placement keeps the right slot for ControlOnOff / status (former Button cards).
		if (placement === 'button') {
			return (
				suppressStatusLabels ||
				(device?.mainActionType === 'none' && isDefaultState && !hasDashRightSlotLabel)
			);
		}

		// No Gateway / No Reports: do not keep a stale metric in the right slot.
		if (suppressStatusLabels && !onOffState) {
			return true;
		}

		if (device?.mainActionType === 'none' && isDefaultState && !hasDashRightSlotLabel) {
			return true;
		}

		// Param-type devices show on/off state on the card but still need
		// the right slot visible for their parameter label
		if (onOffState?.readOnly && device?.mainActionType === 'param') {
			return false;
		}

		if (hasDashRightSlotLabel && !suppressStatusLabels) {
			return false;
		}

		if (isFanDevice(device?.type)) {
			return false;
		}

		// Hide the right slot when:
		// - device has on/off state (card handles toggle)
		// - not in a loading/updating state
		// - no addon switch to display
		// Timer progress is drawn as a card border, not the right slot.
		return !!onOffState && !showRightSlot && !hasAddon;
	}

	beforeUpdate(() => {
		if (device && Object.keys(device).length && Object.keys(deviceState).length) {
			statuses = status_configurator(status, device, settings);
			notAuthenticated = deviceState?.type === 9;
		}
	});

	onDestroy(() => stopTimerTick());

	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: device = $devices[deviceWidget?.id];
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: suppressStatusLabels = hasBleDashStatusInvalidatingNotification(notificationsToShow);
	$: value = !device ? deviceWidget?.id : device?.name;
	$: avatar = getAvatar(device);
	$: isDefaultState = deviceState?.type == defaultStateType;
	$: timerCountdown =
		timerState?.hasTimer && timerState?.timer?.time
			? formatTimerCountdown(timerState.timer.time)
			: undefined;
	$: hasCriticalNotification = criticalNotifications?.length > 0 || suppressStatusLabels;
	$: infoLabelsResult =
		device && status
			? resolveDashDeviceInfoLabels({
					device,
					statuses: status_configurator(status, device, settings),
					status,
					settings,
					offline: isOffline,
					isUpdating,
					notAuthenticated,
					isDefaultState,
					hasCriticalNotification,
					hasTimer: timerState?.hasTimer,
					timerCountdown,
				})
			: {
					showInfoSlot: false,
					labels: [],
					timerInInfoSlot: false,
					rightSlotLabel: null,
					suppressVirtualMainLabel: false,
				};
	$: showInfoSlot = infoLabelsResult.showInfoSlot;
	$: infoLabels = infoLabelsResult.labels;
	$: dashRightSlotLabel = infoLabelsResult.rightSlotLabel;
	$: suppressVirtualMainLabel = infoLabelsResult.suppressVirtualMainLabel;
	// Timer lives in the info row (after power); keep additionalText for blocking states only.
	$: additionalText = getAdditionalText(
		device,
		isUpdating,
		infoLabelsResult.timerInInfoSlot ? false : timerState?.hasTimer,
		infoLabelsResult.timerInInfoSlot ? undefined : timerState?.timer?.time,
		deviceState,
		criticalNotifications,
	);
	// Timer state
	$: storeTimerState =
		device && status && settings
			? getDeviceTimerState(device, status, settings)
			: { hasTimer: false, timer: null, timerStartedAt: undefined };

	$: isLoading = deviceState?.isLoading;
	// Derive on/off state from stores
	$: onOffState =
		device && status ? getDeviceOnOffState(device, status, settings, deviceState?.type) : null;

	$: hasAddon = device && status ? deviceHasAddonSwitch(device, status) : false;

	$: isButtonPlacement = controlPlacement === 'button';
	/**
	 * Button placement needs the right slot for ControlOnOff. Presets like relay-pm
	 * promote power into that slot — demote it back into the info row so the toggle
	 * can render (and the metric is not lost).
	 */
	$: demoteRightSlotForButton =
		isButtonPlacement && !!onOffState && !onOffState.readOnly && !!dashRightSlotLabel;
	$: cardHandlesOnOff = !isButtonPlacement && !!onOffState;
	$: inputStateChip =
		cardHandlesOnOff && device
			? getInputStateInfoLabel($devices[device.id] ?? device, status, settings)
			: null;
	$: displayInfoLabels = (() => {
		const base = demoteRightSlotForButton
			? [dashRightSlotLabel, ...infoLabels].slice(0, 3)
			: infoLabels;
		if (!inputStateChip) return base;
		const rest = (base || []).filter((label) => label?.name !== 'input_state');
		return [inputStateChip, ...rest].slice(0, 3);
	})();
	$: displayShowInfoSlot = showInfoSlot || demoteRightSlotForButton || !!inputStateChip;
	$: quickActionsRightSlotLabel = demoteRightSlotForButton ? null : dashRightSlotLabel;

	$: {
		if (storeTimerState.hasTimer && !loadingRequest && !isLoading && isDefaultState) {
			// Re-sync cache and state when a new timer starts (timerStartedAt changes)
			if (storeTimerState.timerStartedAt !== cachedTimerStartedAt) {
				cachedTimerStartedAt = storeTimerState.timerStartedAt;
				startTimerTick();
			} else if (!tickInterval) {
				startTimerTick();
			}
		} else {
			if (tickInterval) stopTimerTick();
			// // Don't sync stale timer data while awaiting toggle confirmation
			if (!loadingRequest && !isLoading && isDefaultState) {
				timerState = storeTimerState;
			} else {
				timerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
				cachedTimerStartedAt = undefined;
			}
		}
	}

	$: isOffline = !device || !status?.online;

	// isActive follows store state unless a control request is in flight
	$: activeSurfaceOverride = ((): DashDeviceActiveSurfaceOverride | undefined => {
		if (activeSurfacePreset || surfaceGlowOverride) {
			return {
				preset: activeSurfacePreset,
				surfaceGlow: surfaceGlowOverride,
			};
		}
		return undefined;
	})();
	$: activeSurface = resolveDashDeviceActiveSurface(
		{
			device,
			status,
			settings,
			onOffState,
			offline: isOffline,
			deviceStateType: deviceState?.type,
			override: activeSurfaceOverride,
			enableLightColor,
			lightColor,
		},
		getDashLightingPack($dashLightingPackId),
	);
	$: if (!loadingRequest) {
		isActive = activeSurface.active;
	}

	// Pin lock check
	$: pinLocked =
		typeof $pin_unlocked_devices[device?.id] === 'undefined' && $devices[device?.id]?.pin_lock;

	$: isUpdating = deviceState?.type === 3;

	$: showRightSlot = isLoading || loadingRequest || isUpdating;

	$: fwUpdateProgress =
		$updating_devices?.[device?.id]?.prog === undefined
			? 101
			: $updating_devices?.[device?.id]?.prog;
	$: isCamera = isCameraDevice(device);
	$: hideSlotRight = checkShouldHideRightSlot(
		device,
		$edit,
		onOffState,
		hasAddon,
		showRightSlot,
		isDefaultState,
		!!dashRightSlotLabel && !suppressStatusLabels,
		suppressStatusLabels,
		controlPlacement,
	);
	$: isSelected = deviceWidget.id === $sidePanelStateCombined?.id;
	$: timerBorder =
		!isButtonPlacement && timerState?.hasTimer && timerState?.timer
			? {
					percentage: timerState.timer.percentage,
					reverse: isActive,
				}
			: undefined;
	$: cameraCardVariant = isButtonPlacement ? 'button' : 'quick-control';
	// Opaque white fill is the whole-card tap affordance. Button placement keeps
	// glass and shows on/off on the in-slot control instead.
	$: paintCardActive = !isButtonPlacement && !isUpdating && isActive;
</script>

{#if isCamera}
	<DashCameraSingleDeviceCard
		variant={cameraCardVariant}
		{cameraConfig}
		{value}
		{additionalText}
		{editMode}
		hideSlotEditModeAction={!$edit || !$bulkActionEdit}
		{device}
		{status}
		{settings}
		offline={isOffline}
		selected={isSelected}
		on:click={handleClick}
		on:arrow-click
		on:fullscreen
		on:handle-drag-start
		on:handle-drag-end
	>
		<svelte:fragment slot="editModeAction">
			<MainControlStatusSlot
				useV2Components={true}
				{device}
				{status}
				{settings}
				{statuses}
				{disableSelection}
				on:device-select
				on:device-update
				on:handle-drag-start
				on:handle-drag-end
			/>
		</svelte:fragment>
	</DashCameraSingleDeviceCard>
{:else}
	<DashDeviceWidget
		aria-label={$format('aria_parameter_widget_card', { default: 'Parameter widget card' })}
		isFocusable
		arrowRight
		{avatar}
		{value}
		{additionalText}
		offline={isOffline}
		a11yProps={{
			indicatorOnAriaLabel: $format('aria_label_indicator', { default: 'Label indicator' }),
		}}
		{hideSlotRight}
		hideSlotInfo={!displayShowInfoSlot}
		{timerBorder}
		selected={isSelected}
		active={paintCardActive}
		activeSurfaceMode={activeSurface.activeSurfaceMode}
		surfaceGlow={activeSurface.surfaceGlow}
		glowPulse={suppressStatusLabels
			? false
			: activeSurface.presetId === 'light-color'
				? 'subtle'
				: true}
		on:click={handleClick}
		on:arrow-click
		on:handle-drag-start
		on:handle-drag-end
		{editMode}
		{pressHoldOptions}
		hideSlotEditModeAction={!$edit || !$bulkActionEdit}
	>
		<div slot="info" style="display: contents">
			{#each displayInfoLabels as label (label.name)}
				{@const faceActive =
					paintCardActive &&
					!isOffline &&
					activeSurface.activeSurfaceMode === 'opaque' &&
					isOpaqueWhiteActiveBackground(activeSurface.surfaceGlow?.backgroundColor)}
				<LabelV2
					size="m"
					value={label.value}
					icon={label.icon}
					iconColor={faceActive ? '--color-1' : '--color-white'}
					textClass={faceActive ? 'text-color-base' : 'text-white'}
					noBackground={label.noBackground}
					noPadding={label.noPadding}
				/>
			{/each}
		</div>
		<svelte:fragment slot="editModeAction">
			<MainControlStatusSlot
				useV2Components={true}
				{device}
				{status}
				{settings}
				{statuses}
				{disableSelection}
				on:device-select
				on:device-update
			/>
		</svelte:fragment>
		<div
			slot="right"
			class="dash-device-widget-control min-width-0 d-flex justify-content-center align-items-center flex-grow-1"
		>
			<!-- Loading/updating states are rendered here (parent) instead of in DashDeviceQuickActions
			to avoid a flash of the on/off button between isLoading becoming false and the slot being hidden -->
			{#if isUpdating && is_gen_2(device)}
				{#if isWallDisplay(device?.type) && fwUpdateProgress >= 99}
					<div><Loader size="m" class="align-self-center" /></div>
				{:else}
					<CircularProgress
						size="s"
						backgroundColor={$showNewLayout ? '--color-2' : '--color-1'}
						progress={fwUpdateProgress}
						type={fwUpdateProgress > 100 ? 'success' : 'primary'}
					/>
				{/if}
			{:else if showRightSlot}
				<div>
					<Loader size="m" class="align-self-center" />
				</div>
			{:else}
				<DashDeviceQuickActions
					useV2Components={true}
					{device}
					{status}
					{settings}
					{statuses}
					{cardHandlesOnOff}
					{notAuthenticated}
					dashRightSlotLabel={quickActionsRightSlotLabel}
					{suppressVirtualMainLabel}
					{suppressStatusLabels}
				/>
			{/if}
		</div>
	</DashDeviceWidget>
{/if}

{#if !isButtonPlacement && showPinModal}
	<PinUnlock
		bind:showModal={showPinModal}
		on:unlock={handlePinUnlock}
		on:cancel={() => (showPinModal = false)}
	/>
{/if}

{#if !isButtonPlacement && !device?.shared && onOffState && !onOffState.readOnly && !onOffState.disabled}
	<QuickTimeControl
		isOn={isActive}
		icon="fak fa-regular-clock-circle-plus"
		deviceId={device?.id}
		bind:showQuickControlModal
		on:select={(e) => handleTimerSelect(e.detail)}
	/>
{/if}

<!-- Timer progress is a card border; no CircularProgress transition override needed. -->
